import { useLocation } from "wouter";
import SEOHead from "@/components/SEOHead";
import SearchHeader from "@/components/search-header";
import Footer from "@/components/footer";
import { useState, useEffect } from "react";
import { loadAllArtworks, searchArtworks, getRandomArtwork, StaticArtwork } from "@/lib/static-data";
import { trackEvent } from "@/lib/analytics";

function encode(data: Record<string, string>) {
  return Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");
}

function ContactForm() {
  const [fields, setFields] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contact", "bot-field": "", ...fields }),
    })
      .then(() => {
        setStatus("sent");
        trackEvent("contact_form_submit", "engagement", "about_page");
      })
      .catch(() => setStatus("error"));
  };

  if (status === "sent") {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <p className="text-green-800 font-medium">Message sent — thank you! I'll get back to you soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <input type="hidden" name="form-name" value="contact" />
      <p hidden><input name="bot-field" /></p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          name="name"
          required
          value={fields.name}
          onChange={(e) => setFields({ ...fields, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input
          type="email"
          name="email"
          required
          value={fields.email}
          onChange={(e) => setFields({ ...fields, email: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
        <textarea
          name="message"
          required
          rows={5}
          value={fields.message}
          onChange={(e) => setFields({ ...fields, message: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
        />
      </div>
      {status === "error" && (
        <p className="text-red-600 text-sm">Something went wrong. Please try again.</p>
      )}
      <button
        type="submit"
        disabled={status === "sending"}
        className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-medium py-2 px-4 rounded-md transition-colors"
      >
        {status === "sending" ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}

export default function About() {
  const [, setLocation] = useLocation();
  const [comprehensiveArtworks, setComprehensiveArtworks] = useState<
    StaticArtwork[]
  >([]);
  const [searchResults, setSearchResults] = useState<StaticArtwork[]>([]);

  useEffect(() => {
    async function loadInitialData() {
      try {
        const artworks = await loadAllArtworks();
        setComprehensiveArtworks(artworks);
      } catch (error) {
        console.error("Error loading artworks:", error);
      }
    }
    loadInitialData();
  }, []);

  const handleArtworkSelect = (artworkId: number) => {
    window.history.pushState({}, "", `/artwork/${artworkId}/`);
    window.dispatchEvent(new PopStateEvent("popstate"));
    
    // Track artwork view from search
    trackEvent("view_artwork", "navigation", "search_result", artworkId);
  };

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = searchArtworks(comprehensiveArtworks, query, 20);
    setSearchResults(results);

    // Track search event
    trackEvent("search", "artwork_search", query, results.length);
  };

  const handleRandomArtwork = () => {
    if (comprehensiveArtworks.length > 0) {
      const randomArtwork = getRandomArtwork(comprehensiveArtworks);
      if (randomArtwork && randomArtwork.id) {
        window.history.pushState({}, "", `/artwork/${randomArtwork.id}/`);
        window.dispatchEvent(new PopStateEvent("popstate"));
        
        // Track random artwork view
        trackEvent("view_artwork", "navigation", "random_button", randomArtwork.id);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="About - Ariadne's Gallery"
        description="Learn about Ariadne's Gallery, an innovative platform for exploring art through emotional connections and interactive visualizations."
        url="https://ariadnesgallery.com/about/"
        type="website"
      />

      <SearchHeader
        artworks={searchResults}
        onArtworkSelect={handleArtworkSelect}
        onSearch={handleSearch}
        totalCount={comprehensiveArtworks.length}
        onRandomArtwork={handleRandomArtwork}
      />

      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none">
            <h1 className="text-4xl font-bold mb-8">About Ariadne's Gallery</h1>

            <p className="mb-6">
              Ariadne's Gallery is an innovative digital platform that explores
              the emotional landscape of art through interactive visualizations
              and data-driven connections. Named after the mythological figure
              who helped navigate the labyrinth, our gallery helps you navigate
              the complex web of artistic relationships.
            </p>

            <h2 className="text-2xl font-semibold mb-6">The Stendhal Effect</h2>
            <p className="mb-6">
              Our platform is built around the concept of the{" "}
              <strong>Stendhal Syndrome</strong> - the phenomenon where people
              experience overwhelming emotions when exposed to beautiful art.
              We've quantified these emotional responses across thousands of
              artworks, creating a unique map of how different pieces evoke
              similar feelings.
            </p>

            <h2 className="text-2xl font-semibold mb-6">How It Works</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <h3 className="font-semibold mb-3">Emotion Analysis</h3>
                <p className="text-gray-600">
                  Each artwork is analyzed across multiple emotional dimensions
                  including Valence, Arousal, Dominance, Joy, Sadness, Wonder,
                  and more. This creates a unique emotional fingerprint for
                  every piece.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <h3 className="font-semibold mb-3">Knowledge Graph</h3>
                <p className="text-gray-600">
                  Artworks with similar emotional profiles are connected in an
                  interactive network, allowing you to discover unexpected
                  relationships between different artists, movements, and time
                  periods.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <h3 className="font-semibold mb-3">Visual Comparison</h3>
                <p className="text-gray-600">
                  Side-by-side emotion charts let you compare the emotional
                  impact of different artworks, revealing subtle patterns in how
                  artists evoke specific feelings.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <h3 className="font-semibold mb-3">Discovery Tools</h3>
                <p className="text-gray-600">
                  Advanced search and recommendation systems help you find
                  artworks that match your emotional preferences or explore new
                  emotional territories in art.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mb-6">The Collection</h2>
            <p className="mb-6">
              Our database contains over <strong>116,000 artworks</strong>{" "}
              spanning centuries of artistic expression. From Renaissance
              masterpieces to modern abstractions, each work has been carefully
              analyzed to understand its emotional resonance. The collection
              includes:
            </p>

            <ul className="list-disc list-inside mb-8 space-y-2">
              <li>Classical paintings from major European masters</li>
              <li>Impressionist and Post-Impressionist works</li>
              <li>Modern and contemporary pieces</li>
              <li>Works from diverse cultural traditions</li>
              <li>
                Detailed metadata including artist, movement, period, and medium
              </li>
            </ul>

            <h2 className="text-2xl font-semibold mb-6">Data Sources</h2>
            <p className="mb-6">
              Our artwork data is compiled from reputable public sources and
              cultural institutions. All images and metadata are used under
              appropriate licenses, with proper attribution to their original
              sources. We're committed to supporting the preservation and
              accessibility of cultural heritage through digital innovation.
            </p>

            <h2 className="text-2xl font-semibold mb-6">Philosophy</h2>
            <p className="mb-8">
              Art has always been about human connection - between artist and
              viewer, between different cultures and time periods, between
              emotion and expression. Ariadne's Gallery seeks to make these
              connections visible and explorable, creating new pathways for
              understanding and appreciating the rich tapestry of human artistic
              achievement.
            </p>

            <div className="bg-indigo-50 p-6 rounded-lg">
              <p className="text-indigo-800 font-medium">
                Ready to explore? Start your journey through the emotional
                landscape of art by discovering connections between masterpieces
                that span centuries and cultures.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-12 mb-6">Get in touch</h2>
            <p className="mb-6 text-gray-600">
              Questions, feedback, or collaboration ideas? Send a message and I'll get back to you.
            </p>
            <ContactForm />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
